import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useCart } from "../Context/Createcontext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import Popup from "../Components/common/Popup";
import { api_url } from "../config/config";
import addToCartIcon from "../Icons/addToCartIcon.svg";
import wish from "../images/wish.svg";
import wishlistIcon from "../images/wishlist_nofill.svg";
import loader from "../images/loaderimg.png";
import { useWishlist } from "../Context/WishlistContext";

const priceFromVariant = (v, discPct) => {
  if (!v) return { mrp: null, finalPrice: null, showDisc: false, pct: 0 };
  const _mrp = parseFloat(v.mrp) || 0;
  const _disc = parseFloat(v.discounted_price) || 0;
  if (_mrp <= 0)
    return { mrp: null, finalPrice: null, showDisc: false, pct: 0 };
  const showDisc = _disc > 0 && _disc < _mrp;
  return {
    mrp: _mrp,
    finalPrice: showDisc ? _disc : _mrp,
    showDisc,
    pct: showDisc ? discPct || Math.round(((_mrp - _disc) / _mrp) * 100) : 0,
  };
};

const priceFromVariants = (variants, discPct) => {
  if (!variants?.length)
    return { mrp: null, finalPrice: null, showDisc: false, pct: 0 };
  const sorted = [...variants]
    .map((v) => ({
      ...v,
      _m: parseFloat(v.mrp) || 0,
      _d: parseFloat(v.discounted_price) || 0,
    }))
    .filter((v) => v._m > 0)
    .sort((a, b) => a._m - b._m);
  if (!sorted.length)
    return { mrp: null, finalPrice: null, showDisc: false, pct: 0 };
  const best = sorted[0];
  const showDisc = best._d > 0 && best._d < best._m;
  return {
    mrp: best._m,
    finalPrice: showDisc ? best._d : best._m,
    showDisc,
    pct: showDisc
      ? discPct || Math.round(((best._m - best._d) / best._m) * 100)
      : 0,
  };
};

/* ── Helpers ── */
const safeStr = (v) => {
  if (
    v === null ||
    v === undefined ||
    v === "" ||
    v === "null" ||
    v === "undefined"
  )
    return null;
  const s = String(v).trim();
  return s === "" || s === "null" || s === "undefined" ? null : s;
};

const romanToInt = (r) => {
  const m = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let n = 0;
  for (let i = 0; i < r.length; i++)
    m[r[i]] < m[r[i + 1]] ? (n -= m[r[i]]) : (n += m[r[i]]);
  return n;
};
const numVal = (s) => {
  if (!s) return null;
  if (/^[IVXLCDM]+$/.test(s)) return romanToInt(s);
  const m = s.match(/^\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

const tagString = (tags) => {
  if (!tags) return null;
  if (typeof tags === "string") return tags || null;
  if (typeof tags === "object" && !Array.isArray(tags))
    return (
      Object.entries(tags)
        .filter(([, v]) => v)
        .map(([k]) => k.toUpperCase())
        .join(", ") || null
    );
  return null;
};

const parseSafety = (raw) => {
  if (!raw) return [];
  return raw
    .split("|")
    .map((seg) => {
      const colonIdx = seg.indexOf(":");
      if (colonIdx < 0) return null;
      const label = seg.slice(0, colonIdx).replace(/^-\s*/, "").trim();
      const text = seg
        .slice(colonIdx + 1)
        .replace(/<p>/gi, " ")
        .trim();
      return label && text ? { label, text } : null;
    })
    .filter(Boolean);
};

const safetyClr = (text) => {
  const t = (text || "").toUpperCase();
  if (t.includes("UNSAFE"))
    return {
      bg: "bg-red-50",
      border: "border-red-200",
      txt: "text-red-600",
      dot: "bg-red-500",
    };
  if (t.includes("SAFE IF"))
    return {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      txt: "text-yellow-700",
      dot: "bg-yellow-400",
    };
  if (t.includes("SAFE"))
    return {
      bg: "bg-green-50",
      border: "border-green-200",
      txt: "text-green-700",
      dot: "bg-green-500",
    };
  if (t.includes("CAUTION"))
    return {
      bg: "bg-orange-50",
      border: "border-orange-200",
      txt: "text-orange-600",
      dot: "bg-orange-400",
    };
  return {
    bg: "bg-[#eaf4f3]",
    border: "border-[#d4ecea]",
    txt: "text-[#1e4a46]",
    dot: "bg-[#5fbdb3]",
  };
};

/* ── Sub-components ── */
// InfoRow: 11px → 13px label, 13px → 15px value
const InfoRow = ({ label, value }) => {
  const v = safeStr(value);
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#f1f5f5] last:border-0">
      <span className="text-[13px] font-bold uppercase tracking-wider text-[#8faba9] min-w-[130px] shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className={`text-[15px] font-semibold flex-1 leading-snug ${v ? "text-[#374747]" : "text-[#c5d5d4] italic"}`}
      >
        {v || "Not Available"}
      </span>
    </div>
  );
};

// TabBtn: 12px → 14px
const TabBtn = ({ label, active, onClick, icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2.5 text-[14px] font-semibold rounded-xl transition-all duration-200 border-none whitespace-nowrap cursor-pointer
      ${active ? "bg-[#1e4a46] text-white shadow-[0_3px_10px_rgba(22,44,42,0.25)]" : "bg-white text-[#374747] hover:bg-[#eaf4f3] hover:text-[#1e4a46] border border-[#e2eaea]"}`}
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon}
    </svg>
    {label}
  </button>
);

/* ══════════════════════════════════════════════════════════════ */
const ProductCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product_id } = location?.state || {};

  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    cartItems,
    setCartItems,
    handleRemoveFromCart,
  } = useCart();
  const {
    wishlist: wishlistItems,
    addToWishlist,
    removeFromWishlist,
    loading: wishlistLoading,
  } = useWishlist();

  const [product, setProduct] = useState({});
  const [allImages, setAllImages] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [trending, setTrending] = useState([]);
  const [trendPage, setTrendPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const TREND_PER = 8;

  const manufacturer =
    product?.manufacturer?.manufacturer_name ||
    product?.manufacturer?.name ||
    null;

  const sortedVariants = [...(product.variant || [])].sort((a, b) => {
    const va = numVal(a.pack_size),
      vb = numVal(b.pack_size);
    if (va !== null && vb !== null) return va - vb;
    if (va !== null) return -1;
    if (vb !== null) return 1;
    return (a.pack_size || "").localeCompare(b.pack_size || "");
  });

  const currentVariantId =
    selectedVariant?.variant_id ?? product?.variant?.[0]?.variant_id;
  const isWishlisted = currentVariantId
    ? (wishlistItems || []).some(
        (w) =>
          w.product_id === product?.product_id &&
          w.variant_id === currentVariantId,
      )
    : false;

  const { mrp, finalPrice, showDisc, pct } = priceFromVariant(
    selectedVariant || product?.variant?.[0],
    product?.discount_percent,
  );

  const isOOS = (variants) =>
    !Array.isArray(variants) ||
    variants.length === 0 ||
    variants.every((v) => !v.quantity || v.quantity === 0);

  const getCartQty = (vid) => {
    const it = cartItems.find((ci) => ci.variant_id === vid);
    return it ? it.quantity : 0;
  };

  /* ── Fetch product ── */
  useEffect(() => {
    if (!product_id) return;
    setLoading(true);
    fetch(`${api_url}/product/${product_id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "x-auth": "false",
      },
    })
      .then((r) => r.json())
      .then((data) => {
        const d = data.data;
        setProduct(d);
        const primary = d.product_images?.image_url?.primary_image;
        const extra = d.product_images?.image_url?.additional_images || [];
        setAllImages([primary, ...extra].filter(Boolean));
        if (d.variant?.length) {
          const def = [...d.variant].sort(
            (a, b) => (parseFloat(a.mrp) || 0) - (parseFloat(b.mrp) || 0),
          )[0];
          setSelectedVariant(def);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [product_id]);

  /* ── Fetch trending ── */
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${api_url}/cart-list-products`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "x-auth": "false",
          },
        });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (d.data?.length) {
          setTrending(d.data);
          return;
        }
        throw new Error();
      } catch {
        try {
          const r = await fetch(`${api_url}/featured/product?page=1&limit=20`, {
            headers: { "Content-Type": "application/json", "x-auth": "false" },
          });
          const d = await r.json();
          setTrending(d.data || []);
        } catch {}
      }
    };
    load();
  }, []);

  const visibleTrend = trending.slice(
    trendPage * TREND_PER,
    (trendPage + 1) * TREND_PER,
  );
  const totalTrendPg = Math.ceil(trending.length / TREND_PER);

  /* ── Main product cart ── */
  const mainQty = getCartQty(selectedVariant?.variant_id);

  const mainIncrease = async (e) => {
    e.preventDefault();
    if (!selectedVariant?.variant_id) return;
    const nq = mainQty + 1;
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: selectedVariant.product_id,
        quantity: nq,
        variant_id: selectedVariant.variant_id,
      }),
    });
    const d = await res.json();
    if (!res.ok || d.message?.toLowerCase().includes("stock not available")) {
      toast.error("Out of stock");
      return;
    }
    setCartItems((p) =>
      p.map((ci) =>
        ci.variant_id === selectedVariant.variant_id
          ? { ...ci, quantity: nq }
          : ci,
      ),
    );
  };

  const mainDecrease = async (e) => {
    e.preventDefault();
    if (!selectedVariant?.variant_id) return;
    if (mainQty === 1) {
      await handleRemoveFromCart(
        selectedVariant.product_id,
        selectedVariant.variant_id,
      );
      setCartItems((p) =>
        p.filter((ci) => ci.variant_id !== selectedVariant.variant_id),
      );
      return;
    }
    const nq = mainQty - 1;
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: selectedVariant.product_id,
        quantity: nq,
        variant_id: selectedVariant.variant_id,
      }),
    });
    const d = await res.json();
    if (!res.ok || d.message?.toLowerCase().includes("stock not available")) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((p) =>
      p.map((ci) =>
        ci.variant_id === selectedVariant.variant_id
          ? { ...ci, quantity: nq }
          : ci,
      ),
    );
  };

  /* ── Trending cart ── */
  const trendIncrease = async (e, variant, item) => {
    e.preventDefault();
    const qty = getCartQty(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty + 1,
        variant_id: variant.variant_id,
      }),
    });
    const d = await res.json();
    if (!res.ok || d.message?.toLowerCase().includes("stock not available")) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((p) =>
      p.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci,
      ),
    );
  };

  const trendDecrease = async (e, variant) => {
    e.preventDefault();
    const qty = getCartQty(variant.variant_id);
    if (qty === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((p) =>
        p.filter((ci) => ci.variant_id !== variant.variant_id),
      );
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty - 1,
        variant_id: variant.variant_id,
      }),
    });
    const d = await res.json();
    if (!res.ok || d.message?.toLowerCase().includes("stock not available")) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((p) =>
      p.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity - 1 }
          : ci,
      ),
    );
  };

  const goTo = (pid, p) =>
    navigate(`/products/${p.name?.toLowerCase().replace(/\s+/g, "-")}`, {
      state: { product_id: pid },
    });

  const safetyItems = parseSafety(product.safety_advise);
  const tagsStr = tagString(product.product_tags);

  const TABS = [
    {
      key: "description",
      label: "Description",
      icon: (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </>
      ),
    },
    {
      key: "benefits",
      label: "Benefits",
      icon: (
        <>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </>
      ),
    },
    {
      key: "howtouse",
      label: "How to Use",
      icon: (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </>
      ),
    },
    {
      key: "safety",
      label: "Safety Advice",
      icon: (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </>
      ),
    },
    {
      key: "sideeffects",
      label: "Side Effects",
      icon: (
        <>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
        </>
      ),
    },
  ];

  /* ══ LOADING ══ */
  if (loading)
    return (
      <div className="min-h-screen bg-[#f8fafa] flex items-center justify-center font-poppins">
        <div className="flex flex-col items-center gap-4">
          <img src={loader} alt="Loading" className="w-14 h-14 animate-pulse" />
          {/* 13px → 15px */}
          <p className="text-[15px] text-[#8faba9] font-medium">
            Fetching product details…
          </p>
        </div>
      </div>
    );

  /* ══ MAIN RENDER ══ */
  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>
          {product?.name
            ? `${product.name} | MediSYS Pharmacy`
            : "MediSYS Pharmacy"}
        </title>
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="mx-auto px-4 sm:px-6 py-6 sm:mt-0 mt-36">
        {/* ── Breadcrumb ── */}
        {/* 12px → 14px */}
        <nav className="flex items-center gap-2 text-[14px] text-[#8faba9] mb-6 font-medium flex-wrap">
          <Link
            to="/home"
            className="hover:text-[#1e4a46] transition-colors text-[#8faba9]"
            style={{ textDecoration: "none" }}
          >
            Home
          </Link>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 12l4-4-4-4"
              stroke="#8faba9"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to="/products"
            className="hover:text-[#1e4a46] transition-colors text-[#8faba9]"
            style={{ textDecoration: "none" }}
          >
            All Products
          </Link>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 12l4-4-4-4"
              stroke="#8faba9"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[#1e4a46] font-bold truncate max-w-[200px]">
            {product?.name || "Product"}
          </span>
        </nav>

        {/* ══ TOP: Image + Details ══ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* ── Left: Image Panel ── */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-2xl border-[1.5px] border-[#e2eaea] overflow-hidden shadow-[0_4px_24px_rgba(22,44,42,0.09)]">
              {/* Main image */}
              <div className="relative aspect-square flex items-center justify-center bg-gradient-to-br from-[#eaf4f3] via-[#f8fafa] to-[#fff3ee] p-2">
                {isOOS(product.variant) && (
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-2xl">
                    <span className="bg-red-500 text-white text-[15px] font-bold px-5 py-2 rounded-full shadow-lg">
                      Out of Stock
                    </span>
                  </div>
                )}
                {showDisc && pct > 0 && (
                  <div className="absolute top-4 right-4 z-10 bg-[#ff601e] text-white text-[12px] font-black w-12 h-12 rounded-full flex flex-col items-center justify-center leading-tight shadow-[0_4px_14px_rgba(255,96,30,0.5)]">
                    {pct}%<br />
                    OFF
                  </div>
                )}
                <div
                  className="absolute top-4 left-4 z-10 group/wish"
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!localStorage.getItem("authToken")) {
                        setShowLoginPopup(true);
                        return;
                      }
                      isWishlisted
                        ? removeFromWishlist(
                            product.product_id,
                            currentVariantId,
                          )
                        : addToWishlist(
                            product,
                            selectedVariant || product.variant?.[0],
                          );
                    }}
                    disabled={wishlistLoading}
                    className="w-10 h-10 bg-white rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200"
                  >
                    <img
                      src={isWishlisted ? wish : wishlistIcon}
                      alt="wishlist"
                      className="w-5 h-5"
                    />
                  </button>
                  {tooltipOpen && (
                    <div className="absolute left-12 top-1 bg-[#162c2a] text-white text-[12px] font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap z-30 shadow-xl">
                      {isWishlisted
                        ? "Remove from wishlist"
                        : "Add to wishlist"}
                    </div>
                  )}
                </div>

                <div className="w-full h-full overflow-hidden">
                  {allImages.length > 0 ? (
                    <img
                      src={allImages[imgIdx]}
                      alt={product?.name || "Product"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.svg";
                      }}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-zoom-in"
                    />
                  ) : (
                    <img
                      src="/default.svg"
                      alt="No image"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setImgIdx((p) => Math.max(0, p - 1))}
                      disabled={imgIdx === 0}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm disabled:opacity-30 hover:border-[#5fbdb3] transition-all cursor-pointer"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#374747"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 12H5M12 5l-7 7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() =>
                        setImgIdx((p) => Math.min(allImages.length - 1, p + 1))
                      }
                      disabled={imgIdx === allImages.length - 1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm disabled:opacity-30 hover:border-[#5fbdb3] transition-all cursor-pointer"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#374747"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail strip */}
              <div className="flex gap-2.5 px-4 py-3 justify-center flex-wrap border-t border-[#f1f5f5] min-h-[76px] items-center">
                {allImages.length === 0 && (
                  <div className="w-14 h-14 rounded-xl border border-[#e2eaea] bg-[#f8fafa] flex items-center justify-center">
                    <img
                      src="/default.svg"
                      alt="no-img"
                      className="w-8 h-8 object-contain opacity-25"
                    />
                  </div>
                )}
                {allImages.length === 1 && (
                  <button className="w-14 h-14 rounded-xl overflow-hidden ring-2 ring-[#1e4a46] border-2 border-[#1e4a46] cursor-default">
                    <img
                      src={allImages[0]}
                      alt="thumb"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.svg";
                      }}
                      className="w-full h-full object-contain p-1"
                    />
                  </button>
                )}
                {allImages.length > 1 &&
                  allImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setImgIdx(i)}
                      className={`w-14 h-14 rounded-xl overflow-hidden transition-all cursor-pointer ${i === imgIdx ? "ring-2 ring-[#1e4a46] border-2 border-[#1e4a46]" : "border border-[#e2eaea] bg-[#f8fafa]"}`}
                    >
                      <img
                        src={img || "/default.svg"}
                        alt={`thumb-${i}`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default.svg";
                        }}
                        className="w-full h-full object-contain p-1"
                      />
                    </button>
                  ))}
              </div>
            </div>

            {/* Quick-info strip */}
          </div>

          {/* ── Middle: Info Panel ── */}
          <div className="flex gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="bg-white rounded-2xl border-[1.5px] border-[#e2eaea] border-l-4 border-l-[#5fbdb3] overflow-hidden shadow-[0_4px_24px_rgba(22,44,42,0.08)]">
                <div className="px-6 py-5">
                  {/* Status badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {product.prescription_required === "No" && (
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest text-[#1e4a46] bg-[#eaf4f3] border border-[#d4ecea] rounded-full px-3 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#5fbdb3]" />
                        OTC — No Prescription
                      </span>
                    )}
                    {product.prescription_required === "Yes" && (
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest text-[#ff601e] bg-[#fff3ee] border border-[#ffd5c2] rounded-full px-3 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#ff601e]" />
                        Prescription Required
                      </span>
                    )}
                    {product.chronic_type && (
                      <span className="text-[12px] font-bold uppercase tracking-widest text-[#374747] bg-[#f8fafa] border border-[#e2eaea] rounded-full px-3 py-1">
                        {product.chronic_type}
                      </span>
                    )}
                    {tagsStr && (
                      <span className="text-[12px] font-bold uppercase tracking-widest text-[#ff601e] bg-[#fff3ee] border border-[#ffd5c2] rounded-full px-3 py-1">
                        {tagsStr}
                      </span>
                    )}
                  </div>

                  <h1
                    className="text-[26px] sm:text-[30px] font-black text-[#0a1715] leading-tight mb-1"
                    style={{ fontFamily: "'Nunito',sans-serif" }}
                  >
                    {product?.name || "Medicine Name"}
                  </h1>
                  {(product.generic_name ||
                    product.dosage_form ||
                    product.consume_type) && (
                    <p className="text-[15px] text-[#8faba9] font-medium mb-4">
                      {[
                        product.generic_name,
                        product.dosage_form,
                        product.consume_type,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}

                  <div className="h-px bg-[#f1f5f5] mb-4" />

                  {/* Price */}
                  <div className="mb-5">
                    {mrp ? (
                      <div className="flex items-start gap-5">
                        {showDisc && (
                          <div>
                            <p className="text-[12px] font-bold uppercase tracking-wider text-[#8faba9] mb-0.5">
                              MRP
                            </p>
                            <p className="text-[20px] font-semibold text-[#8faba9] line-through leading-none">
                              ₹{mrp}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-[12px] font-bold uppercase tracking-wider text-[#1e4a46] mb-0.5">
                            {showDisc ? "You Pay" : "Price"}
                          </p>
                          <div className="flex items-center gap-2.5">
                            <span
                              className="text-[40px] font-black text-[#1e4a46] leading-none"
                              style={{ fontFamily: "'Nunito',sans-serif" }}
                            >
                              ₹{finalPrice}
                            </span>
                            {showDisc && pct > 0 && (
                              <span className="bg-[#ff601e] text-white text-[13px] font-bold px-2.5 py-1 rounded-full shadow-[0_3px_10px_rgba(255,96,30,0.35)]">
                                {pct}% OFF
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[20px] font-bold text-[#8faba9]">
                        Price not available
                      </span>
                    )}
                  </div>

                  {/* Pack size selector */}
                  {sortedVariants.length > 1 && (
                    <div className="mb-5">
                      <p className="text-[13px] font-bold uppercase tracking-wider text-[#8faba9] mb-2">
                        Pack Size
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {sortedVariants.map((v) => {
                          const isActive =
                            selectedVariant?.variant_id === v.variant_id;
                          const {
                            mrp: vM,
                            finalPrice: vF,
                            showDisc: vD,
                          } = priceFromVariant(v, product.discount_percent);
                          const oos = !v.quantity || v.quantity === 0;
                          return (
                            <button
                              key={v.variant_id}
                              onClick={() => !oos && setSelectedVariant(v)}
                              disabled={oos}
                              className={`flex flex-col items-start px-3.5 py-2.5 rounded-xl border-[1.5px] transition-all duration-200 cursor-pointer
                      ${isActive ? "border-[#1e4a46] bg-[#eaf4f3] shadow-[0_2px_10px_rgba(22,44,42,0.15)]" : "border-[#e2eaea] bg-white hover:border-[#5fbdb3]"}
                      ${oos ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <span
                                className={`text-[14px] font-bold ${isActive ? "text-[#1e4a46]" : "text-[#374747]"}`}
                              >
                                {safeStr(v.pack_size) || "Default"}
                              </span>
                              {vM && (
                                <span
                                  className={`text-[13px] font-semibold mt-0.5 ${isActive ? "text-[#5fbdb3]" : "text-[#8faba9]"}`}
                                >
                                  ₹{vF}
                                  {vD && (
                                    <span className="text-[#8faba9] line-through ml-1 text-[12px]">
                                      ₹{vM}
                                    </span>
                                  )}
                                </span>
                              )}
                              {oos && (
                                <span className="text-[11px] text-red-400 font-semibold mt-0.5">
                                  Out of stock
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Brand + category chips */}
                  <div className="flex flex-col gap-2 mb-5">
                    {manufacturer && (
                      <span className="flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-xl bg-[#f8fafa] border border-[#e2eaea] text-[#374747] w-fit">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#8faba9"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        {manufacturer}
                      </span>
                    )}
                    <span className="text-[13px] font-semibold px-3 py-1.5 rounded-xl bg-[#f8fafa] border border-[#e2eaea] text-[#374747] w-fit">
                      Generic Name: {product.generic_name || "Not Available"}
                    </span>
                    {(product.category_ids || []).map((c) => (
                      <span
                        key={c.category_id}
                        className="text-[13px] font-semibold px-3 py-1.5 rounded-full bg-[#eaf4f3] border border-[#d4ecea] text-[#1e4a46] w-fit"
                      >
                        {c.category_name}
                      </span>
                    ))}
                    {(product.concern_ids || []).map((c) => (
                      <span
                        key={c.concern_id}
                        className="text-[13px] font-semibold px-3 py-1.5 rounded-full bg-[#fff3ee] border border-[#ffd5c2] text-[#ff601e] w-fit"
                      >
                        {c.concern_name}
                      </span>
                    ))}
                  </div>

                  {/* Add to Cart */}
                  {!isOOS(product.variant) ? (
                    <div className="flex items-center gap-3">
                      {mainQty > 0 ? (
                        <div className="flex items-center bg-[#eaf4f3] border-[1.5px] border-[#d4ecea] rounded-xl overflow-hidden h-12">
                          <button
                            onClick={mainDecrease}
                            className="w-12 h-12 text-2xl font-black text-[#1e4a46] bg-transparent border-none cursor-pointer hover:bg-white transition-colors flex items-center justify-center leading-none"
                          >
                            −
                          </button>
                          <span
                            className="text-[19px] font-black text-[#1e4a46] min-w-[40px] text-center"
                            style={{ fontFamily: "'Nunito',sans-serif" }}
                          >
                            {mainQty}
                          </span>
                          <button
                            onClick={mainIncrease}
                            className="w-12 h-12 text-2xl font-black text-[#1e4a46] bg-transparent border-none cursor-pointer hover:bg-white transition-colors flex items-center justify-center leading-none"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            if (!localStorage.getItem("authToken")) {
                              setShowLoginPopup(true);
                              return;
                            }
                            handleAddToCart(product, selectedVariant);
                          }}
                          className="flex items-center justify-center gap-2.5 bg-[#1e4a46] hover:bg-[#ff601e] text-white text-[16px] font-bold px-8 h-12 rounded-xl border-none cursor-pointer shadow-[0_4px_16px_rgba(22,44,42,0.28)] hover:shadow-[0_6px_22px_rgba(255,96,30,0.4)] transition-all duration-200 hover:-translate-y-0.5"
                        >
                          <img
                            src={addToCartIcon}
                            alt="Cart"
                            className="w-4 h-4"
                          />
                          Add to Cart
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!localStorage.getItem("authToken")) {
                            setShowLoginPopup(true);
                            return;
                          }
                          isWishlisted
                            ? removeFromWishlist(
                                product.product_id,
                                currentVariantId,
                              )
                            : addToWishlist(
                                product,
                                selectedVariant || product.variant?.[0],
                              );
                        }}
                        className="w-12 h-12 rounded-xl border-[1.5px] border-[#e2eaea] bg-white flex items-center justify-center hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200 flex-shrink-0 cursor-pointer"
                      >
                        <img
                          src={isWishlisted ? wish : wishlistIcon}
                          alt="wishlist"
                          className="w-5 h-5"
                        />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="text-[15px] font-semibold text-red-600">
                        Currently Out of Stock — Check back later
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: "HSN Code", value: safeStr(product.hsn_code) },
                  { label: "Manufacturer", value: safeStr(manufacturer) },
                  {
                    label: "Storage",
                    value: safeStr(product.storage_instruction),
                  },
                  {
                    label: "Prescription",
                    value: safeStr(product.prescription_required),
                  },
                ]
                  .filter((s) => s.value)
                  .map((s) => (
                    <div
                      key={s.label}
                      className="bg-white rounded-xl border border-[#e2eaea] px-4 py-4 min-h-[85px] flex flex-col justify-center"
                    >
                      <p className="text-[12px] font-bold uppercase tracking-wider text-[#8faba9] mb-0.5">
                        {s.label}
                      </p>
                      <p className="text-[14px] font-bold text-[#1e4a46] leading-snug">
                        {s.value}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Product details table */}
          </div>

          {/* ── Right: Product Details Table ── */}
          <div className="bg-white rounded-2xl border-[1.5px] border-[#e2eaea] border-l-4 border-l-[#ff601e] overflow-hidden">
            <div className="px-5 py-2.5 bg-[#fff3ee] border-b border-[#ffd5c2]">
              <span className="text-[14px] font-bold uppercase tracking-widest text-[#ff601e]">
                Product Details
              </span>
            </div>
            <div className="px-5 py-1">
              <InfoRow label="Dosage Form" value={product.dosage_form} />
              <InfoRow label="Primary Use" value={product.primary_use} />
              <InfoRow label="Consume Type" value={product.consume_type} />
              <InfoRow label="Drug Status" value={product.drug_status} />
              <InfoRow
                label="Returnable"
                value={
                  typeof product.is_returnable === "boolean"
                    ? product.is_returnable
                      ? "Yes"
                      : "No"
                    : null
                }
              />
              <InfoRow
                label="Discontinued"
                value={
                  typeof product.discontinued === "boolean"
                    ? product.discontinued
                      ? "Yes"
                      : "No"
                    : null
                }
              />
              <InfoRow label="Packing" value={product.packing} />
            </div>
          </div>
        </div>

        {/* ══ TABS ══ */}
        <div className="bg-white rounded-2xl border-[1.5px] border-[#e2eaea] overflow-hidden mb-8 shadow-[0_4px_20px_rgba(22,44,42,0.06)]">
          {/* Tab bar */}
          <div className="px-5 py-4 border-b border-[#f1f5f5] overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {TABS.map((t) => (
                <TabBtn
                  key={t.key}
                  label={t.label}
                  active={activeTab === t.key}
                  onClick={() => setActiveTab(t.key)}
                  icon={t.icon}
                />
              ))}
            </div>
          </div>
          {/* Tab content */}
          <div className="px-6 py-6 min-h-[120px]">
            {activeTab === "description" &&
              (product.description ? (
                // 14px → 16px
                <p className="text-[16px] text-[#374747] leading-[1.9]">
                  {product.description}
                </p>
              ) : (
                // 13px → 15px
                <p className="text-[15px] text-[#8faba9] italic">
                  No description available for this medicine.
                </p>
              ))}
            {activeTab === "benefits" &&
              (product.benefits ? (
                // 14px → 16px (applied via inline style since it's dangerouslySetInnerHTML)
                <div
                  className="text-[16px] text-[#374747] leading-[1.9]"
                  dangerouslySetInnerHTML={{
                    __html: product.benefits
                      .replace(/\\u003cbr>/gi, "<br/>")
                      .replace(/<br>/gi, "<br/>"),
                  }}
                />
              ) : (
                // 13px → 15px
                <p className="text-[15px] text-[#8faba9] italic">
                  No benefits information available. Consult your pharmacist.
                </p>
              ))}
            {activeTab === "howtouse" &&
              (product.how_to_use ? (
                // 14px → 16px
                <div
                  className="text-[16px] text-[#374747] leading-[1.9]"
                  dangerouslySetInnerHTML={{
                    __html: product.how_to_use
                      .replace(/\\u003cbr>/gi, "<br/>")
                      .replace(/<br>/gi, "<br/>"),
                  }}
                />
              ) : (
                // 13px → 15px
                <p className="text-[15px] text-[#8faba9] italic">
                  Usage instructions not specified. Consult your doctor or
                  pharmacist.
                </p>
              ))}
            {activeTab === "safety" &&
              (safetyItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {safetyItems.map((s, i) => {
                    const c = safetyClr(s.text);
                    return (
                      <div
                        key={i}
                        className={`rounded-xl border p-4 ${c.bg} ${c.border}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`}
                          />
                          {/* 11px → 13px */}
                          <span
                            className={`text-[13px] font-black uppercase tracking-widest ${c.txt}`}
                          >
                            {s.label}
                          </span>
                        </div>
                        {/* 12px → 14px */}
                        <p className="text-[14px] text-[#374747] leading-relaxed">
                          {s.text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // 13px → 15px
                <p className="text-[15px] text-[#8faba9] italic">
                  No safety advisory found. Always take as directed by your
                  doctor.
                </p>
              ))}
            {activeTab === "sideeffects" &&
              (product.side_effects ? (
                // 14px → 16px
                <p className="text-[16px] text-[#374747] leading-[1.9]">
                  {product.side_effects}
                </p>
              ) : (
                // 13px → 15px
                <p className="text-[15px] text-[#8faba9] italic">
                  No known side effects recorded. Report unexpected reactions to
                  your doctor.
                </p>
              ))}
          </div>
        </div>

        {/* ══ TRENDING / FEATURED PRODUCTS ══ */}
        {trending.length > 0 && (
          <div className="bg-white rounded-2xl border-[1.5px] border-[#e2eaea] overflow-hidden shadow-[0_4px_20px_rgba(22,44,42,0.06)]">
            <div className="px-6 py-4 border-b border-[#f1f5f5] flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-4 h-[2px] bg-[#ff601e] rounded-full" />
                  {/* 10px → 12px */}
                  <span className="text-[12px] font-bold uppercase tracking-widest text-[#ff601e]">
                    Explore More
                  </span>
                </div>
                {/* 18px → 20px */}
                <h2
                  className="text-[20px] font-black text-[#1e4a46]"
                  style={{ fontFamily: "'Nunito',sans-serif" }}
                >
                  Trending Products
                </h2>
              </div>
              {totalTrendPg > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTrendPage((p) => Math.max(0, p - 1))}
                    disabled={trendPage === 0}
                    className="w-9 h-9 rounded-xl border border-[#e2eaea] bg-white flex items-center justify-center disabled:opacity-30 hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all cursor-pointer"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#374747"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() =>
                      setTrendPage((p) => Math.min(totalTrendPg - 1, p + 1))
                    }
                    disabled={trendPage >= totalTrendPg - 1}
                    className="w-9 h-9 rounded-xl border border-[#e2eaea] bg-white flex items-center justify-center disabled:opacity-30 hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all cursor-pointer"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#374747"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="p-4 overflow-x-auto">
              <div className="grid grid-cols-8 gap-3 min-w-[1200px]">
                {visibleTrend.map((item, index) => {
                  const variants = item.variant || [];
                  const {
                    mrp: tM,
                    finalPrice: tF,
                    showDisc: tD,
                    pct: tP,
                  } = priceFromVariants(variants, item.discount_percent);
                  const tQty = getCartQty(variants?.[0]?.variant_id);
                  const oos =
                    !variants.length ||
                    variants.every((v) => !v.quantity || v.quantity === 0);
                  const isOrange = index % 2 === 1;
                  const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
                  const btnCls = isOrange
                    ? "bg-[#ff601e] hover:bg-[#e55519]"
                    : "bg-[#1e4a46] hover:bg-[#ff601e]";
                  const borderCls = isOrange
                    ? "border-[#ffd5c2] hover:border-[#ff601e]/50"
                    : "border-[#e2eaea] hover:border-[#5fbdb3]";
                  const mfrName =
                    item.manufacturer?.manufacturer_name ||
                    item.manufacturer?.name ||
                    null;

                  return (
                    <div
                      key={item.product_id || index}
                      className={`group border-[1.5px] ${borderCls} bg-white rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1.5 transition-all duration-300 shadow-[0_2px_12px_rgba(22,44,42,0.06)] hover:shadow-[0_8px_28px_rgba(22,44,42,0.14)]`}
                      style={{
                        animation: `pcFadeUp 0.3s ease ${index * 50}ms both`,
                      }}
                    >
                      {/* Image */}
                      <div
                        className={`w-full h-[150px] ${imgBg} relative cursor-pointer group overflow-hidden`}
                        onClick={() => goTo(item.product_id, item)}
                      >
                        {tD && tP > 0 && (
                          <div className="absolute top-2 right-2 bg-[#ff601e] text-white text-[10px] font-bold w-7 h-7 rounded-full flex flex-col items-center justify-center leading-tight z-10">
                            {tP}%<br />
                            OFF
                          </div>
                        )}

                        <img
                          src={
                            item?.product_images?.image_url?.primary_image ||
                            "/default.svg"
                          }
                          alt={item.name || "Product"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/default.svg";
                          }}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                      <div className="w-full h-px bg-[#f1f5f5]" />
                      {/* Info */}
                      <div
                        className="px-3 pt-2.5 pb-1 flex-1 cursor-pointer"
                        onClick={() => goTo(item.product_id, item)}
                      >
                        {/* 11px → 13px */}
                        <p className="text-[13px] font-semibold text-[#1e4a46] line-clamp-2 leading-snug mb-1 group-hover:text-[#ff601e] transition-colors">
                          {item.name || "Medicine"}
                        </p>
                        {mfrName && (
                          // 10px → 12px
                          <p className="text-[12px] text-[#8faba9] truncate mb-1">
                            {mfrName}
                          </p>
                        )}
                        {/* Price */}
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          {tM ? (
                            <>
                              {tD && (
                                // 10px → 12px
                                <span className="text-[12px] text-[#8faba9] line-through">
                                  ₹{tM}
                                </span>
                              )}
                              {/* 14px → 16px */}
                              <span
                                className="text-[16px] font-black text-[#1e4a46]"
                                style={{ fontFamily: "'Nunito',sans-serif" }}
                              >
                                ₹{tF}
                              </span>
                              {tD && tP > 0 && (
                                // 9px → 11px
                                <span
                                  className={`text-[11px] font-bold ${isOrange ? "text-[#ff601e]" : "text-[#5fbdb3]"}`}
                                >
                                  {tP}% off
                                </span>
                              )}
                            </>
                          ) : (
                            // 11px → 13px
                            <span className="text-[13px] text-[#8faba9]">
                              0.00
                            </span>
                          )}
                        </div>
                        {oos && (
                          // 9px → 11px
                          <span className="text-[11px] font-bold text-red-400 block mt-0.5">
                            Out of Stock
                          </span>
                        )}
                      </div>
                      {/* Cart */}
                      <div className="px-3 pb-3 pt-1">
                        {tQty > 0 ? (
                          <div className="flex items-center justify-between bg-[#eaf4f3] border border-[#d4ecea] rounded-xl h-8 px-2">
                            <button
                              onClick={(e) =>
                                trendDecrease(e, variants[0], item)
                              }
                              className="w-6 h-6 flex items-center justify-center text-[#1e4a46] font-black text-base hover:bg-white rounded-lg transition-colors cursor-pointer"
                            >
                              −
                            </button>
                            {/* 12px → 14px */}
                            <span className="font-black text-[14px] text-[#1e4a46]">
                              {tQty}
                            </span>
                            <button
                              onClick={(e) =>
                                trendIncrease(e, variants[0], item)
                              }
                              className="w-6 h-6 flex items-center justify-center text-[#1e4a46] font-black text-base hover:bg-white rounded-lg transition-colors cursor-pointer"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          // 10px → 12px
                          <button
                            className={`w-full h-8 ${btnCls} text-white text-[13px] font-bold rounded-[10px] flex items-center justify-center gap-1 transition-all duration-200 border-none cursor-pointer disabled:bg-[#c5d5d4] disabled:cursor-not-allowed`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!localStorage.getItem("authToken")) {
                                setShowLoginPopup(true);
                                return;
                              }
                              if (!oos && variants[0])
                                await handleAddToCart(item, variants[0], 1);
                            }}
                            disabled={oos || !variants[0]}
                          >
                            <img
                              src={addToCartIcon}
                              alt="Cart"
                              className="w-3 h-3 object-contain"
                            />
                            {oos ? "Out of Stock" : "Add to Cart"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}

      <style>{`
        @keyframes pcFadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-thumb { background:#d4ecea; border-radius:4px; }
      `}</style>
    </div>
  );
};

export default ProductCard;
